import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["ariaControls", "current", "onSearchChange", "search", "hideSearch"],
  _excluded2 = ["autoFilter", "hideResults", "itemComponent", "minimumSearchCount", "onSearchChange", "options", "search", "enableKeyboardScrollingNav", "typeaheadResultsTokenProps"];
import ShareInput from '../ShareInput/ShareInput';
import { isOptionGroup } from '../../types/OptionTypes';
import { createRef, PureComponent } from 'react';
import partial from 'react-utils/partial';
import { syntheticEvent } from '../../utils/SyntheticEvent';
import { getNoMatchesOption, getSearchRegExp } from './Typeahead.utils';
import { TypeaheadResults } from './TypeaheadResults';
import { TypeaheadResultsGroup } from './TypeaheadResultsGroup';
import { TypeaheadResultsItem } from './TypeaheadResultsItem';
import { TypeaheadSearch } from './TypeaheadSearch';
import { uniqueId } from '../../../transitional/utils/uniqueId';
import styled from 'foundations-theming/utils';
import { Controllable } from '../Controllable';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTypeaheadResultsItem = styled.div.withConfig({
  displayName: "Typeahead__StyledTypeaheadResultsItem"
})(({
  $isInGroup,
  theme
}) => {
  return {
    ['& > :first-child']: {
      paddingInlineStart: $isInGroup ? theme.space['500'] : 'inherit'
    }
  };
});
function getFlattenedOptions(groupedOptions) {
  const groups = [];
  const options = [];
  groupedOptions.forEach(option => {
    if (isOptionGroup(option)) {
      const groupIdx = groups.push(option) - 1;
      option.options.forEach(groupOption => {
        options.push(Object.assign({}, groupOption, {
          groupIdx
        }));
      });
    } else {
      options.push(Object.assign({}, option, {
        groupIdx: -1
      }));
    }
  });
  return {
    options,
    groups
  };
}
function getHighlightedIndex(component, filtered) {
  return filtered.options.indexOf(component.getHighlighted(filtered));
}
const KeyHandlers = {
  ArrowDown: component => {
    const filtered = component.getFilteredOptions();
    const {
      options
    } = filtered;
    const nextIndex = (getHighlightedIndex(component, filtered) + 1) % options.length;
    component.setState({
      highlighted: options[nextIndex],
      highlightedId: component.getResultItemId(options[nextIndex])
    });
  },
  // The KeyDown handler that uses these handlers expects a KeyBoardEvent,
  // but the handleChange method expects a MouseEvent.
  Enter: (component, evt) => {
    component.handleChange(component.getHighlighted(component.getFilteredOptions()), evt);
  },
  ArrowUp: component => {
    const filtered = component.getFilteredOptions();
    const {
      options
    } = filtered;
    const index = getHighlightedIndex(component, filtered);
    const nextIndex = index === 0 ? options.length - 1 : index - 1;
    component.setState({
      highlighted: options[nextIndex],
      highlightedId: component.getResultItemId(options[nextIndex])
    });
  }
};
export class TypeaheadCore extends PureComponent {
  static getDerivedStateFromProps(props, state) {
    const {
      autoFilter = true,
      options,
      search = ''
    } = props;
    let derivedState = {};
    if (options !== state.prevOptions) {
      derivedState = Object.assign({}, derivedState, {
        flattened: getFlattenedOptions(options),
        prevOptions: options
      });
    }
    if (state.prevAutoFilter && search !== state.prevSearch) {
      derivedState = Object.assign({}, derivedState, {
        searchRegex: search && getSearchRegExp(search),
        prevSearch: search
      });
    } else if (search !== state.prevSearch) {
      derivedState = Object.assign({}, derivedState, {
        prevSearch: search
      });
    }
    if (autoFilter !== state.prevAutoFilter) {
      derivedState = Object.assign({}, derivedState, {
        prevAutoFilter: autoFilter
      });
    }
    return derivedState;
  }
  constructor(props) {
    super(props);
    this.handleChange = (option, evt) => {
      if (option.disabled) {
        return;
      }
      this.handleHighlight(option, evt);
      const {
        onChange
      } = this.props;
      if (typeof onChange === 'function') {
        onChange(syntheticEvent(option.value, evt));
      }
      if (typeof option.onClick === 'function') {
        option.onClick(evt);
      }
    };
    this.handleHighlight = (option, evt) => {
      const {
        highlighted
      } = this.state;
      if (highlighted === option) {
        return;
      }
      this.setState({
        highlighted: option,
        highlightedId: evt.currentTarget.id
      });
    };
    this.handleKeyDown = evt => {
      const {
        key
      } = evt;
      const handler = KeyHandlers[key] ? KeyHandlers[key] : null;
      if (handler) {
        evt.preventDefault();
        handler(this, evt);
      }
    };
    const {
      autoFilter = true,
      options,
      search = ''
    } = props;
    this.state = {
      flattened: getFlattenedOptions(options),
      highlighted: null,
      highlightedId: null,
      prevAutoFilter: autoFilter,
      prevOptions: options,
      prevSearch: search,
      searchRegex: search && autoFilter ? getSearchRegExp(search) : undefined
    };
    this._highlightedRef = /*#__PURE__*/createRef();
  }
  componentDidUpdate() {
    const highlightedNode = this._highlightedRef.current;
    if (!highlightedNode) {
      return;
    }
    const {
      offsetHeight,
      offsetParent,
      offsetTop
    } = highlightedNode;
    if (!offsetParent) return;
    const scrollTop = offsetParent.scrollTop;
    if (scrollTop > offsetTop) {
      offsetParent.scrollTop = offsetTop;
    }
    const nodeBottom = offsetHeight + offsetTop;
    const parentBottom = scrollTop + offsetParent.offsetHeight;
    if (parentBottom >= nodeBottom) {
      return;
    }
    offsetParent.scrollTop = scrollTop + nodeBottom - parentBottom;
  }
  getFilteredOptions() {
    const {
      searchRegex,
      flattened
    } = this.state;
    const results = searchRegex ? flattened.options.filter(({
      dropdownText,
      text,
      value
    }) => text && searchRegex.test(text) || searchRegex.test(`${value}`) || typeof dropdownText === 'string' && searchRegex.test(dropdownText)) : flattened.options;
    return {
      groups: flattened.groups,
      options: results.length ? results : [getNoMatchesOption()]
    };
  }
  getHighlighted(filtered) {
    const {
      value
    } = this.props;
    const {
      highlighted
    } = this.state;
    if (highlighted && filtered.options.indexOf(highlighted) > -1) {
      return highlighted;
    }
    return filtered.options.find(option => option.value === value) || filtered.options[0] || null;
  }
  getResultItemId(option) {
    return `${this._id}_${this.state.flattened.options.indexOf(option)}`;
  }
  getSearchCount() {
    const {
      flattened
    } = this.state;
    if (!flattened || !flattened.options) {
      return -1;
    }
    return flattened.options.length + flattened.groups.length;
  }
  renderFilteredOptions() {
    const filtered = this.getFilteredOptions();
    const {
      groups
    } = filtered;
    const inGroup = false;
    const highlighted = this.getHighlighted(filtered);
    let groupIdx = -1;
    let groupedOptions = [];
    const options = [];
    const {
      enableKeyboardScrollingNav
    } = this.props;
    filtered.options.forEach((option, itemIndex) => {
      if (option.groupIdx !== groupIdx) {
        if (groupedOptions.length) {
          options.push(this.renderResultsGroup(groups[groupIdx], groupedOptions, highlighted, enableKeyboardScrollingNav, groupIdx));
        }
        groupIdx = option.groupIdx || 0;
        groupedOptions = [];
      }
      if (option.groupIdx === -1) {
        options.push(this.renderResultsItem(option, highlighted, enableKeyboardScrollingNav, inGroup, itemIndex));
      } else {
        groupedOptions.push(option);
      }
    });
    if (groupedOptions.length) {
      options.push(this.renderResultsGroup(groups[groupIdx], groupedOptions, highlighted, enableKeyboardScrollingNav, groupIdx));
    }
    return options;
  }
  renderResults({
    hideResults,
    typeaheadResultsTokenProps
  }) {
    if (hideResults) {
      return null;
    }
    return /*#__PURE__*/_jsx(TypeaheadResults, Object.assign({}, typeaheadResultsTokenProps, {
      children: this.renderFilteredOptions()
    }));
  }
  renderResultsGroup(group, options, highlighted, enableKeyboardScrollingNav, groupIndex) {
    const inGroup = true;
    return /*#__PURE__*/_jsx(TypeaheadResultsGroup, {
      group: group,
      children: options.map((option, itemIndex) => this.renderResultsItem(option, highlighted, enableKeyboardScrollingNav, inGroup, itemIndex, groupIndex))
    }, String(group.value || group.options[0].value));
  }
  renderResultsItem(option, highlighted, enableKeyboardScrollingNav, isInGroup, itemIndex, groupIndex = -1) {
    if (option === getNoMatchesOption()) {
      return this.renderResultsEmpty();
    }
    const isHighlighted = highlighted === option;
    const {
      itemComponent
    } = this.props;
    return /*#__PURE__*/_jsx(StyledTypeaheadResultsItem, {
      $isInGroup: isInGroup,
      children: /*#__PURE__*/_jsx(TypeaheadResultsItem, {
        highlighted: isHighlighted,
        itemComponent: itemComponent,
        onClick: partial(this.handleChange, option),
        onMouseEnter: partial(this.handleHighlight, option),
        option: option,
        ref: isHighlighted ? this._highlightedRef : undefined,
        search: this.state.searchRegex,
        id: this.getResultItemId(option),
        enableKeyboardScrollingNav: enableKeyboardScrollingNav
      })
    }, `${groupIndex}-${itemIndex}`);
  }
  renderResultsEmpty() {
    const {
      itemComponent
    } = this.props;
    return /*#__PURE__*/_jsx(TypeaheadResultsItem, {
      disabled: true,
      itemComponent: itemComponent,
      option: getNoMatchesOption()
    }, String(getNoMatchesOption().value));
  }
  renderSearch(_ref) {
    let {
        ariaControls,
        current,
        onSearchChange,
        search = '',
        hideSearch
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return /*#__PURE__*/_jsx(TypeaheadSearch, Object.assign({}, rest, {
      "aria-activedescendant": current,
      "aria-controls": ariaControls,
      onChange: onSearchChange,
      onKeyDown: this.handleKeyDown,
      value: search,
      __hideSearch: hideSearch
    }));
  }
  render() {
    const _this$props = this.props,
      {
        autoFilter: __autoFilter = true,
        hideResults = false,
        itemComponent: __itemComponent = 'li',
        minimumSearchCount: __minimumSearchCount = 0,
        onSearchChange,
        search = '',
        typeaheadResultsTokenProps
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded2);
    const {
      highlightedId
    } = this.state;
    this._id = this._id || uniqueId('typeahead-');
    const hideSearch = __minimumSearchCount && this.getSearchCount() < __minimumSearchCount;
    const renderedSearch = this.renderSearch(Object.assign({
      ariaControls: this._id,
      current: highlightedId || undefined,
      onSearchChange,
      search,
      hideSearch: Boolean(hideSearch)
    }, rest));
    const renderedResults = this.renderResults({
      hideResults,
      typeaheadResultsTokenProps
    });
    return /*#__PURE__*/_jsxs("div", {
      "aria-expanded": true,
      "aria-haspopup": "listbox",
      "aria-controls": this._id,
      role: "combobox",
      children: [renderedSearch, renderedResults]
    });
  }
}
export const Typeahead = ShareInput(Controllable(TypeaheadCore, ['search', 'value']));

// @ts-expect-error noMatchesI18nKey does not exist on ShareInput
Typeahead.noMatchesI18nKey = 'salesUI.UITypeahead.noMatchesFound';
// @ts-expect-error noOptionsI18nKey does not exist on ShareInput
Typeahead.noOptionsI18nKey = 'salesUI.UITypeahead.noOptions';
Typeahead.displayName = 'Typeahead';